/**
 * CompetitionPriceListDialog.jsx — every New-list service at one size band,
 * against VirTour Media, Framed by JG and 805 Media Works. The general price
 * check, opened from the Quotes header with no quote behind it.
 *
 * Bands are the website order page's (0 to 999, 500 sq ft steps, 10,000+). The
 * math is buildPriceListAnalysis; the cells are the order modal's.
 */

import { useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Table, TableHead, TableBody, TableRow, TableCell, Chip, TextField, MenuItem } from '@mui/material';
import { fmtC, fmtD } from '../../lib/utils';
import { useMobile } from '../../hooks/useMobile';
import { buildPriceListAnalysis, SQFT_BANDS } from '../../lib/competitionAnalysis';
import { CompetitorCell, head, small } from './CompetitionAnalysisDialog';

const DEFAULT_BAND = SQFT_BANDS.findIndex((b) => b.label.startsWith('2,000'));

const ALONE_LABEL = { included: 'included', none: 'not offered', unmapped: 'no match', inquire: 'inquire' };

// What a product, or theirs, costs as the whole order, where that differs.
function Alone({ cell, price }) {
  const text = cell
    ? (cell.status === 'priced' ? fmtC(cell.price) : ALONE_LABEL[cell.status])
    : price !== undefined ? (price == null ? 'quoted' : fmtC(price)) : null;
  if (!text) return null;
  return (
    <Typography component="div" sx={{ ...small, color: 'text.secondary', mt: 0.25 }}>On its own: {text}</Typography>
  );
}

function Ours({ row }) {
  return (
    <>
      <Typography component="div" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
        {row.quoted ? 'Quoted' : fmtC(row.ours)}
      </Typography>
      {row.unit && <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>{row.unit}</Typography>}
      <Alone price={row.oursAlone} />
      {row.flash && (
        <Typography component="div" sx={{ ...small, color: 'text.secondary' }}>{row.flash.name}: {fmtC(row.flash.price)}</Typography>
      )}
    </>
  );
}

export default function CompetitionPriceListDialog({ onClose }) {
  const { isMobile } = useMobile();
  const [band, setBand] = useState(DEFAULT_BAND);
  const { groups, competitors, sqft } = useMemo(() => buildPriceListAnalysis(SQFT_BANDS[band].sqft), [band]);

  const cellFor = (row, c) => (
    <>
      <CompetitorCell cell={row.cells[c.id]} ours={row.quoted ? 0 : row.ours} />
      <Alone cell={row.alone?.[c.id]} />
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">Competition analysis</Typography>
        <Typography variant="body2" color="text.secondary">
          Every service at one size, against their current prices, line by line, no package deals
        </Typography>
        {isMobile ? (
          // Twenty chips would fill half a phone screen; a picker keeps the list in view.
          <TextField select size="small" fullWidth label="Square footage" value={band} onChange={(e) => setBand(Number(e.target.value))} sx={{ mt: 1.5 }}>
            {SQFT_BANDS.map((b, i) => <MenuItem key={b.label} value={i}>{b.label} sq ft</MenuItem>)}
          </TextField>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {SQFT_BANDS.map((b, i) => (
              <Chip
                key={b.label}
                label={b.label}
                size="small"
                color={i === band ? 'primary' : 'default'}
                variant={i === band ? 'filled' : 'outlined'}
                onClick={() => setBand(i)}
              />
            ))}
          </Box>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Typography component="div" sx={{ ...small, color: 'text.secondary', mb: 1.5 }}>
          {SQFT_BANDS[band].label} sq ft. Priced as part of a full order, so add-on rates apply; “On its own” is the price when it’s the whole order.
          {' '}Their rungs: {competitors.map((c) => `${c.name} ${c.tierLabel(sqft)} (prices ${fmtD(c.asOf)})`).join(' · ')}.
        </Typography>

        {isMobile ? (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {groups.map((g) => (
              <Box key={g.id} sx={{ display: 'grid', gap: 1 }}>
                <Typography sx={{ ...head, color: 'text.secondary', mt: 1 }}>{g.name}</Typography>
                {g.rows.map((r) => (
                  <Box key={r.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{r.isAddon ? '+ ' : ''}{r.desc}</Typography>
                      <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}><Ours row={r} /></Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '84px 1fr', gap: '8px 10px' }}>
                      {competitors.map((c) => [
                        <Typography key={`${c.id}-l`} component="div" sx={{ ...small, color: 'text.secondary', fontWeight: 600, pt: 0.25 }}>{c.name}</Typography>,
                        <Box key={`${c.id}-v`}>{cellFor(r, c)}</Box>,
                      ])}
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
            <Table size="small" stickyHeader sx={{ minWidth: 980, '& td': { verticalAlign: 'top' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...head, width: '22%', bgcolor: 'background.paper' }}>Your service</TableCell>
                  <TableCell align="right" sx={{ ...head, width: '12%', bgcolor: 'background.paper' }}>Yours</TableCell>
                  {competitors.map((c) => (
                    <TableCell key={c.id} sx={{ ...head, width: '22%', bgcolor: 'background.paper' }}>
                      {c.name}
                      <Typography component="div" sx={{ ...small, textTransform: 'none', fontWeight: 400, color: 'text.secondary' }}>
                        {c.tierLabel(sqft)}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => [
                  <TableRow key={`g-${g.id}`} sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={2 + competitors.length} sx={{ ...head, py: 0.75 }}>{g.name}</TableCell>
                  </TableRow>,
                  ...g.rows.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell sx={{ fontSize: '0.82rem', pl: r.isAddon ? 3.5 : 2 }}>{r.isAddon ? '+ ' : ''}{r.desc}</TableCell>
                      <TableCell align="right"><Ours row={r} /></TableCell>
                      {competitors.map((c) => <TableCell key={c.id}>{cellFor(r, c)}</TableCell>)}
                    </TableRow>
                  )),
                ])}
              </TableBody>
            </Table>
          </Box>
        )}

        <Typography component="div" sx={{ ...small, color: 'text.secondary', mt: 1.5 }}>
          Green means they charge more than you; red means they charge less. Competitor prices are read at the middle of the band.
          {' '}Framed by JG counts ADUs, guest houses and garages in their square footage. 805 Media Works only sells add-ons beside one of their main services.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
